import { ExpandMore } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Divider,
    List,
    ListItem,
    ListItemText,
    Skeleton,
    Typography,
} from '@mui/material';
import { InfiniteData } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import React from 'react';
import { EvacuationPeopleList } from '../../api/types';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { useTranslations } from '../Translations/TranslationsProvider';

interface EventScannedPeopleProps {
    peopleDataPaginated:
        | InfiniteData<{
              data: EvacuationPeopleList;
              nextPage: any;
          }>
        | undefined;
    isLoadingPeople: boolean;
    isFetchingNextPage: boolean;
    hasNextPage: boolean | undefined;
    fetchNextPage: () => void;
}

export const EvacuationScannedPeople: React.FC<EventScannedPeopleProps> = ({
    peopleDataPaginated,
    isLoadingPeople,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
}) => {
    const { translations } = useTranslations();

    const peopleData = peopleDataPaginated?.pages.flatMap((page) => page.data);
    const totalResults = peopleData?.flatMap((data) => data.totalResults)[0];
    const people = peopleData?.flatMap((data) => data.persons);

    return (
        <Accordion data-testid="event-scanned-people-accordion" sx={{ mb: 2 }} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">
                    {translations.EVACUATIONS_SCANNED_PEOPLE} {isLoadingPeople ? '' : `(${totalResults})`}
                </Typography>
            </AccordionSummary>

            <Divider />

            <AccordionDetails>
                {isLoadingPeople ? (
                    <>
                        {[1, 2, 3].map((i) => (
                            <Box key={i} display="flex" gap={2} alignItems="center" my={1}>
                                <Skeleton variant="circular" width={40} height={40} />
                                <Box flex={1}>
                                    <Skeleton variant="text" width="60%" />
                                    <Skeleton variant="text" width="40%" />
                                </Box>
                            </Box>
                        ))}
                    </>
                ) : totalResults === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                        {translations.EVACUATIONS_EMPTY_SCANNED_PEOPLE_LIST}
                    </Typography>
                ) : (
                    <List disablePadding>
                        {people?.map((person, index) => (
                            <Box key={index}>
                                <ListItem
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: 0,
                                        gap: '8px',
                                    }}
                                >
                                    <ListItemText
                                        primary={`${person.personFirstName} ${person.personLastName}`}
                                        secondary={person.personOrgUnit}
                                    />
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'end' }}>
                                        <Typography variant="body2" sx={{ fontSize: '12px', textAlign: 'end' }}>
                                            {translations.EVACUATION_BY} {person.personCheckedInBy}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontSize: '12px', textAlign: 'end' }}>
                                            {formatDistanceToNow(new Date(person.personTimestamp), { addSuffix: true })}
                                        </Typography>
                                    </Box>
                                </ListItem>
                                {index < totalResults! - 1 && <Divider component="li" sx={{ my: 1 }} />}
                            </Box>
                        ))}
                    </List>
                )}
                {hasNextPage && (
                    <Box textAlign="center" mt={2}>
                        {isFetchingNextPage ? (
                            <LoadingSpinner />
                        ) : (
                            <Button onClick={() => fetchNextPage()}>{translations.GLOBAL_LOAD_MORE_BUTTON}</Button>
                        )}
                    </Box>
                )}
            </AccordionDetails>
        </Accordion>
    );
};
